import { type Theme } from '#app/theme/index.js';
import { type TextInputValue, type TextInputVariant } from './types.js';
import { deserializeValue, serializeValue } from './utilities.js';
import { ReactNode, RefCallback, RefObject, useCallback } from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { css, styled } from 'styled-components';

const getColor = ({
  disabled,
  placeholder,
  readOnly,
  theme,
}: {
  disabled: boolean;
  placeholder: boolean;
  readOnly: boolean;
  theme: Theme;
}): string => {
  if (readOnly) {
    return theme.colorsV2.gray80;
  }

  if (disabled) {
    return theme.colorsV2.gray40;
  }

  if (placeholder) {
    return theme.colorsV2.gray60;
  }

  return theme.colorsV2.black;
};

const getType = ({
  variant,
}: {
  variant: TextInputVariant;
}): React.HTMLInputTypeAttribute => {
  if (variant === 'number' || variant === 'phone') {
    return 'number';
  }

  if (variant === 'date') {
    return 'date';
  }

  return 'text';
};

type InputProps<TVariant extends TextInputVariant> = {
  readonly allowDecimals?: TVariant extends 'usd'
    ? boolean
    : TVariant extends 'number'
      ? boolean
      : TVariant extends 'percent'
        ? boolean
        : never;
  readonly ariaLabel?: string;
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly disabled: boolean;
  readonly hasActionArea?: boolean;
  readonly hasError?: boolean;
  readonly id?: string;
  readonly inputRef?:
    | RefCallback<HTMLInputElement>
    | RefObject<HTMLInputElement>;
  readonly labelText?: null | ReactNode | string;
  readonly largeAndCentered?: TVariant extends 'usd' ? boolean : never;
  readonly max?: number;
  readonly min?: TVariant extends 'number'
    ? number
    : TVariant extends 'date'
      ? string
      : never;
  readonly name?: string;
  readonly onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Opinionated version of onChange that handles a lot of heavy lifting
   * but may prove incompatible with other opinionated frameworks like Downshift
   */
  readonly onValueChange?: (value?: TextInputValue<TVariant>) => void;
  readonly placeholder?: string;
  readonly readOnly: boolean;
  readonly resize?: TVariant extends 'textArea'
    ? 'both' | 'horizontal' | 'none' | 'vertical' | null
    : never;
  readonly rows?: TVariant extends 'textArea' ? number : never;
  readonly value?: TextInputValue<TVariant>;
  readonly variant: TVariant;
};

const Element = styled.input<
  Omit<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    InputProps<any> & {
      hasActionArea?: boolean;
    },
    'onChange'
  >
>(
  ({
    disabled,
    hasActionArea = false,
    largeAndCentered,
    readOnly,
    resize,
    variant,
  }) => css`
    ${({ theme }) => css`
      flex: 1;
      width: 100%;
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      color: ${getColor({ disabled, placeholder: false, readOnly, theme })};
      

      transition: border-color 0.2s ease-out;

      ${
        variant === 'menu' && !disabled && !readOnly
          ? css`
              cursor: pointer;
            `
          : undefined
      }

      ${
        variant === 'textArea' && !resize
          ? css`
              resize: none;
            `
          : css`
              resize: ${resize};
            `
      }

      ${
        largeAndCentered &&
        css`
          text-align: center;
          font-size: 48px;
          line-height: 60px;
        `
      }

      ${
        hasActionArea &&
        css`
          padding-right: 24px;
        `
      }

      &::placeholder {
        color: ${getColor({ disabled, placeholder: true, readOnly, theme })};
        opacity: 1;
      }

      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      &[type=number] {
        -moz-appearance: textfield;
      }
    }
    `}
  `,
);

export const Input = <TVariant extends TextInputVariant>({
  allowDecimals,
  ariaLabel,
  autoComplete,
  hasActionArea,
  inputRef,
  largeAndCentered,
  max,
  min,
  name,
  onChange,
  onValueChange,
  resize,
  rows,
  variant,
  ...props
}: InputProps<TVariant>) => {
  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (onChange) {
        onChange(event);
      }

      const value = serializeValue({
        allowDecimals: allowDecimals ?? false,
        event,
        variant,
      });

      if (
        variant === 'number' &&
        typeof value === 'number' &&
        typeof min === 'number' &&
        typeof max === 'number' &&
        (value < min || value > max)
      ) {
        return;
      }

      onValueChange?.(value);
    },
    [allowDecimals, max, min, onChange, onValueChange, variant],
  );

  return (
    <Element
      autoComplete={autoComplete}
      {...props}
      aria-disabled={props.disabled || props.readOnly}
      aria-invalid={props.hasError}
      aria-label={
        ariaLabel ??
        (typeof props.labelText === 'string' ? props.labelText : undefined)
      }
      aria-readonly={props.readOnly}
      as={variant === 'textArea' ? ReactTextareaAutosize : 'input'}
      disabled={props.disabled || props.readOnly}
      hasActionArea={hasActionArea}
      largeAndCentered={largeAndCentered}
      max={max}
      min={min}
      minRows={variant === 'textArea' ? (rows ?? 2) : undefined}
      name={name}
      onChange={handleChange}
      ref={inputRef}
      resize={resize}
      type={getType({ variant })}
      value={deserializeValue({
        addCurrencyPrefix: largeAndCentered,
        allowDecimals: allowDecimals ?? false,
        value: props.value,
        variant,
      })}
      variant={variant}
    />
  );
};
